// On-device OCR to prefill the "Evaluate a listing" form from a screenshot.
// Tesseract.js is loaded lazily from a CDN and runs entirely in the browser —
// the image is processed locally and never leaves the device (only the static
// OCR library + language model are fetched). Best-effort: it prefills what it
// can read; the user verifies and corrects.

/* eslint-disable @typescript-eslint/no-explicit-any */
let tessPromise: Promise<any> | null = null;
function loadTesseract(): Promise<any> {
  if (tessPromise) return tessPromise;
  tessPromise = new Promise((resolve, reject) => {
    const w = window as any;
    if (w.Tesseract) { resolve(w.Tesseract); return; }
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js";
    s.onload = () => resolve((window as any).Tesseract);
    s.onerror = () => reject(new Error("could not load the OCR library"));
    document.head.append(s);
  });
  return tessPromise;
}

export interface ParsedListing {
  price?: number;
  areaSqft?: number;
  areaSqm?: number;
  tenure?: "freehold" | "leasehold";
  district?: string; // "15"
  floor?: number;
  remainingLease?: number;
  propertyType?: string;
  // Location anchors — the whole point of the model: tie a listing to the
  // actual project/street so it can be compared against past transactions there.
  project?: string; // e.g. "The Sail @ Marina Bay" (private developments)
  street?: string; // e.g. "Marina Boulevard" / "Ang Mo Kio Ave 10"
  address?: string; // best full address line, for display / reference
  block?: string; // HDB block, e.g. "456A"
  town?: string; // HDB town (matched against a known-town list)
  flatType?: string; // HDB flat type, e.g. "4 ROOM" / "EXECUTIVE"
  postal?: string; // 6-digit SG postal code
  unitNumber?: string; // e.g. "#34-12"
  // Transit access, when the listing states it (e.g. "950 m (11 min) from
  // TE26 Marine Parade MRT"). Matters most for buyers who don't drive.
  mrtName?: string; // e.g. "Marine Parade"
  mrtCode?: string; // e.g. "TE26"
  mrtDistanceM?: number; // metres to the station, if given
  mrtWalkMin?: number; // walking minutes, if given
  // Strata / MCST figures (from an AGM balance sheet or a §47 certificate).
  maintenanceFee?: number; // monthly maintenance + sinking, S$
  sinkingBalance?: number; // sinking-fund balance, S$
  units?: number; // number of units in the development
  rawText: string;
}

export interface ParseOpts {
  // Known HDB towns (UPPERCASE), so an HDB screenshot resolves its town for
  // comps. Passed by the HDB evaluate panel, which has the canonical list.
  towns?: readonly string[];
}

export async function scanListing(file: File, opts: ParseOpts = {}): Promise<ParsedListing> {
  const Tesseract = await loadTesseract();
  const { data } = await Tesseract.recognize(file, "eng");
  const text: string = data?.text ?? "";
  return parseListing(text, opts);
}

// Singapore road-name suffixes, used to spot a street/address line.
const ROAD_SUFFIX =
  "Road|Rd|Street|St|Avenue|Ave|Lane|Ln|Drive|Dr|Close|Cl|Crescent|Cres|Walk|Way|Terrace|Ter|" +
  "Boulevard|Blvd|Hill|Rise|Link|Grove|Gardens|Garden|Park|Quay|View|Place|Pl|Loop|Green|" +
  "Central|Circle|Circuit|Heights|Height|Vale|Bank|Field|Farmway|Ridge|Mount|Crest|Bay";
const ROAD_RE = new RegExp(
  `\\b([A-Z][A-Za-z'.]+(?:\\s+[A-Z0-9][A-Za-z0-9'.]+){0,4}\\s+(?:${ROAD_SUFFIX}))\\b(\\s+\\d+[A-Z]?)?`,
);
// Malay-prefix road names ("Jalan Lempeng", "Lorong 5 Toa Payoh") have no
// English suffix, so match them by their leading word instead.
const ROAD_PREFIX_RE =
  /\b((?:Jalan|Lorong|Lengkok|Lengkong|Taman|Kampong|Kampung|Bukit)\s+[A-Z0-9][A-Za-z0-9'.]+(?:\s+[A-Z0-9][A-Za-z0-9'.]+){0,3})/;

// Generic listing-page headers / portal chrome that are never a project name.
const PROJECT_STOP = new Set([
  "listing", "for sale", "sale", "new launch", "featured", "property", "properties",
  "details", "overview", "description", "home", "sold", "price", "location", "about",
  "gallery", "photos", "map", "contact", "agent", "save", "share", "verified", "resale",
  // Portal / app UI chrome seen at the top of screenshots.
  "propertyguru", "99.co", "99co", "ninety nine", "edgeprop", "srx", "srx property",
  "ohmyhome", "huttons", "orangetee", "propnex", "get app", "whatsapp", "directions",
  "call", "listed", "listed on", "results", "search", "filters", "sort", "menu",
]);

// Portal chrome that shows up *inside* a noisy OCR line (not as the whole line),
// e.g. "¢& Search results (J UB" from a page header, or a "Viewed" badge overlaid
// on the photo. Matched as whole words anywhere in the line so a garbled header
// is never mistaken for the project name.
const PROJECT_CHROME_RE =
  /\b(?:search\s*results?|results?|filters?|sort(?:\s*by)?|listed(?:\s*on)?|viewed|shortlist|nearby|directions|whats\s*app|get\s*app|new\s*launch|for\s*sale|sponsored|featured|showing|map\s*view|street\s*view|save(?:d)?\s*search)\b/i;

// Is this line a plausible project name? Rejects prices, specs, types, addresses
// and portal chrome. Returns the cleaned name, or null.
function projectCandidate(raw: string): string | null {
  const NEG =
    /\$|\bpsf\b|\bsq\s?\.?\s?(ft|m)\b|\bsqft\b|\bsqm\b|bed|bath|for\s+sale|asking|lease|free\s?hold|\bdistrict\b|\bd\d{1,2}\b|built|\bmrt\b|maintenance|storey|floor\s*area|remaining|\bhdb\b|\broom\b|nearby/i;
  const TYPE_ONLY = /^(condominium|apartment|executive\s+condominium|terrace|semi[-\s]?detached|detached|for\s+sale)$/i;
  const line = raw.replace(/^[•·\-\s]+|[•·\-\s]+$/g, "").trim();
  if (line.length < 3 || line.length > 48) return null;
  if (NEG.test(line)) return null;
  if (TYPE_ONLY.test(line)) return null;
  if (/^(?:blk|block)\b/i.test(line)) return null; // address, not a project
  if (/^\d/.test(line)) return null; // leading number → street address
  if (/\(\d{6}\)|\bsingapore\s+\d{6}\b/i.test(line)) return null; // has a postal
  if (/\bTown\b$/i.test(line)) return null; // "Ang Mo Kio Town" → area, not project
  if (PROJECT_STOP.has(line.toLowerCase())) return null; // whole-line header word
  if (PROJECT_CHROME_RE.test(line)) return null; // portal chrome inside the line
  if (!/[A-Za-z].*[A-Za-z]/.test(line)) return null; // at least two letters
  return line;
}

// Detect the project name from a listing. On portal listings the development
// name sits between the headline price and the street address (agent names and
// portal chrome come *above* the price), so prefer the name-like line just above
// the address, then the first name-like line after the price, and only then fall
// back to the leading lines. Skipped for HDB (no project concept).
function detectProject(
  lines: string[],
  isHdb: boolean,
  priceIdx: number,
  addressIdx: number,
): string | undefined {
  if (isHdb) return undefined;
  // 1) The line(s) immediately above the address are almost always the project.
  if (addressIdx > 0) {
    for (let i = addressIdx - 1; i >= 0 && i >= addressIdx - 3; i--) {
      const cand = projectCandidate(lines[i]);
      if (cand) return cand;
    }
  }
  // 2) Otherwise the first name-like line after the headline price.
  if (priceIdx >= 0) {
    for (const raw of lines.slice(priceIdx + 1, priceIdx + 9)) {
      const cand = projectCandidate(raw);
      if (cand) return cand;
    }
  }
  // 3) Fallback: the first name-like line near the top.
  for (const raw of lines.slice(0, 8)) {
    const cand = projectCandidate(raw);
    if (cand) return cand;
  }
  return undefined;
}

// Singapore road-name detection, shared by project detection (to find the
// address line) and by the street/address scan below.
const stripLead = (l: string) =>
  l.replace(/^\s*(?:blk|block)\.?\s*\d+[A-Z]?\s+/i, "").replace(/^\s*\d+[A-Z]?\s+(?=[A-Z])/, "");
function roadOf(l: string): string | null {
  const s = stripLead(l);
  const m = ROAD_RE.exec(s);
  if (m) return (m[1] + (m[2] ?? "")).replace(/\s+/g, " ").trim();
  const p = ROAD_PREFIX_RE.exec(s);
  return p ? p[1].replace(/\s+/g, " ").trim() : null;
}
// A "strong" address line carries a leading block/house number, or a postal or
// district on the same line — a real street line, not a project headline.
const isStrongAddress = (line: string) =>
  /^\s*(?:blk|block|\d)/i.test(line) ||
  /\(\d{6}\)|\bsingapore\s+\d{6}\b|\bd(?:istrict)?\s?\d{1,2}\b/i.test(line);

// Heuristic field extraction from noisy OCR text of a property listing.
export function parseListing(text: string, opts: ParseOpts = {}): ParsedListing {
  const t = text.replace(/ /g, " ");
  const out: ParsedListing = { rawText: text };
  const lines = t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const isHdb = /\bhdb\b|\b\d\s?room\b|\bexecutive\s+(?:flat|apartment)\b|\bhousing\s+board\b/i.test(t);

  // Price: prefer "$1,470,000"; else "1.47M" / "1.47 mil". The headline price
  // is typically the largest plausible figure on the page.
  const prices: number[] = [];
  for (const m of t.matchAll(/\$\s?([\d][\d,]{4,})/g)) {
    const n = Number(m[1].replace(/,/g, ""));
    if (n >= 100_000 && n <= 30_000_000) prices.push(n);
  }
  for (const m of t.matchAll(/([\d]+(?:\.\d+)?)\s*(?:m|mil|million)\b/gi)) {
    const n = Math.round(Number(m[1]) * 1_000_000);
    if (n >= 100_000 && n <= 30_000_000) prices.push(n);
  }
  if (prices.length) out.price = Math.max(...prices);

  // Area — the unit token is a frequent OCR casualty ("sqft" → "saft"/"sqf"/
  // "soft"), so tolerate common variants.
  const sqft = t.match(/([\d,]{3,})\s*(?:sq\s?\.?\s?ft|sq\s?ft|sqft|sqf|s[oa]ft|ft²|ft2|square\s*feet)\b/i);
  if (sqft) { const n = Number(sqft[1].replace(/,/g, "")); if (n >= 100 && n <= 100_000) out.areaSqft = n; }
  const sqm = t.match(/([\d,]{2,})\s*(?:sq\s?\.?\s?m|sq\s?m|sqm|m²|m2|square\s*met)\b/i);
  if (sqm) { const n = Number(sqm[1].replace(/,/g, "")); if (n >= 10 && n <= 10_000) out.areaSqm = n; }

  // Fallback: derive floor area from price ÷ price-per-sqft, e.g.
  // "S$ 1,368.88 psf". Portal listings almost always show the psf even when the
  // raw sqft figure is missing or garbled, so this reliably recovers the area.
  if (!out.areaSqft && !out.areaSqm && out.price) {
    const psfM = t.match(/([\d,]+(?:\.\d+)?)\s*psf\b/i);
    const psf = psfM ? Number(psfM[1].replace(/,/g, "")) : NaN;
    if (Number.isFinite(psf) && psf >= 100 && psf <= 20_000) {
      const ft = Math.round(out.price / psf);
      if (ft >= 100 && ft <= 100_000) out.areaSqft = ft;
    }
  }

  // Tenure
  if (/free\s?hold/i.test(t)) out.tenure = "freehold";
  else if (/lease\s?hold|9{2,3}[\s-]?(?:year|yr)/i.test(t)) out.tenure = "leasehold";

  // District e.g. "D15" / "District 15"
  const d = t.match(/\bD(?:istrict)?\s?[- ]?(\d{1,2})\b/i);
  if (d) { const n = Number(d[1]); if (n >= 1 && n <= 28) out.district = String(n).padStart(2, "0"); }

  // Floor from a Singapore unit number e.g. "#10-123"
  const fl = t.match(/#\s?(\d{1,2})\s?[- ]/);
  if (fl) out.floor = Number(fl[1]);

  // Remaining lease
  const ls = t.match(/(\d{2,3})\s*(?:year|yr)s?\s*(?:lease|left|remaining|balance)/i)
    || t.match(/(?:balance|remaining)\s*(?:lease\s*)?(\d{2,3})\s*(?:year|yr)/i);
  if (ls) { const y = Number(ls[1]); if (y > 0 && y <= 999) out.remainingLease = y; }

  // Property type (most specific first)
  for (const ty of ["Executive Condominium", "Condominium", "Apartment", "Semi-Detached", "Terrace", "Detached"]) {
    if (new RegExp(ty.replace(/[- ]/g, "[- ]?"), "i").test(t)) { out.propertyType = ty; break; }
  }

  // Strata / MCST figures (best-effort — AGM balance sheets vary widely).
  const fee = t.match(/maintenance(?:\s*(?:fee|charge)s?)?[^$\d]{0,20}\$?\s?([\d,]{2,})/i);
  if (fee) { const n = Number(fee[1].replace(/,/g, "")); if (n >= 50 && n <= 20_000) out.maintenanceFee = n; }
  const sink = t.match(/sinking\s*fund[^$\d]{0,30}\$?\s?([\d,]{4,})/i);
  if (sink) { const n = Number(sink[1].replace(/,/g, "")); if (n >= 1_000) out.sinkingBalance = n; }
  const units = t.match(/([\d,]{2,4})\s*(?:total\s*)?units?\b/i);
  if (units) { const n = Number(units[1].replace(/,/g, "")); if (n >= 2 && n <= 5_000) out.units = n; }

  // ---- Location anchors ---------------------------------------------------
  // Postal code: "Singapore 118998" / "S(123456)" / "(123456)".
  const postal = t.match(/\bsingapore\s+(\d{6})\b/i) || t.match(/\bS\(?(\d{6})\)?/) || t.match(/\((\d{6})\)/);
  if (postal) out.postal = postal[1];

  // HDB block: "Blk 456" / "Block 123A".
  const blk = t.match(/\b(?:blk|block)\.?\s*(\d{1,4}[A-Z]?)\b/i);
  if (blk) out.block = blk[1].toUpperCase();

  // Full unit number, e.g. "#34-12" (floor already derived separately).
  const unit = t.match(/#\s?(\d{1,3})\s?-\s?(\d{1,4}[A-Z]?)\b/);
  if (unit) out.unitNumber = `#${unit[1]}-${unit[2]}`;

  // Project name (private developments only). On portal listings it sits between
  // the headline price and the street address, so locate those first: the price
  // line, and the address line (a strong street line if there is one, else the
  // first road-bearing line).
  const priceIdx = out.price
    ? lines.findIndex((l) => /\$\s?[\d][\d,]{4,}|\b\d+(?:\.\d+)?\s*(?:m|mil|million)\b/i.test(l))
    : -1;
  let addressIdx = lines.findIndex((l) => !!roadOf(l) && isStrongAddress(l));
  if (addressIdx < 0) addressIdx = lines.findIndex((l) => !!roadOf(l));
  const project = detectProject(lines, isHdb, priceIdx, addressIdx);
  if (project) out.project = project;

  // Street / address: scan lines for a Singapore road-name suffix, preferring a
  // genuine address line (leading block/house-number, or a postal/district on
  // the line) over the project headline (which may itself end in "…Bay/Park").
  const scored = lines
    .map((line) => ({ line, road: roadOf(line) }))
    .filter((x): x is { line: string; road: string } => !!x.road)
    .map((x) => {
      const strong = isStrongAddress(x.line);
      const isProjectLine = out.project && x.line.includes(out.project);
      return { ...x, rank: (strong ? 2 : 0) + (isProjectLine ? -1 : 1) };
    })
    .sort((a, b) => b.rank - a.rank);
  if (scored.length) {
    out.street = scored[0].road;
    out.address = scored[0].line.replace(/^[•·\-\s]+/, "").replace(/\s*[•·|].*$/, "").trim();
  } else if (out.postal) {
    out.address = `Singapore ${out.postal}`;
  }

  // HDB town: match the known-town list against the text (longest match wins,
  // so "ANG MO KIO" beats a stray "KIO").
  if (opts.towns?.length) {
    const up = t.toUpperCase();
    let best = "";
    for (const town of opts.towns) {
      if (town.length > best.length && new RegExp(`\\b${town.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`).test(up)) best = town;
    }
    if (best) out.town = best;
  }

  // Transit hint: "950 m (11 min) from TE26 Marine Parade MRT", "5 min walk to
  // Marine Parade MRT", "Marine Parade MRT (TE26)". Best-effort; the app can
  // still resolve the nearest MRT via OneMap when the listing omits it.
  const mrtDist = t.match(/([\d,]{2,5})\s*m\s*(?:\((\d{1,3})\s*min[^)]*\))?\s*(?:from|to)\s*(?:([A-Z]{2,3}\d{1,3})\s+)?([A-Z][A-Za-z'.\- ]+?)\s*MRT/i);
  const mrtWalk = t.match(/(\d{1,3})\s*min(?:ute)?s?\s*(?:walk\s*)?(?:to|from)\s*(?:([A-Z]{2,3}\d{1,3})\s+)?([A-Z][A-Za-z'.\- ]+?)\s*MRT/i);
  const mrtNamed = t.match(/([A-Z][A-Za-z'.\- ]+?)\s*MRT(?:\s*Station)?\s*\(([A-Z]{2,3}\d{1,3})\)/i);
  const cleanName = (s: string) => s.replace(/\b(from|to|near|nearest)\b/gi, "").replace(/\s+/g, " ").trim();
  if (mrtDist) {
    const d = Number(mrtDist[1].replace(/,/g, ""));
    if (Number.isFinite(d) && d > 0 && d < 10_000) out.mrtDistanceM = d;
    if (mrtDist[2]) out.mrtWalkMin = Number(mrtDist[2]);
    if (mrtDist[3]) out.mrtCode = mrtDist[3].toUpperCase();
    if (mrtDist[4]) out.mrtName = cleanName(mrtDist[4]);
  } else if (mrtWalk) {
    out.mrtWalkMin = Number(mrtWalk[1]);
    if (mrtWalk[2]) out.mrtCode = mrtWalk[2].toUpperCase();
    if (mrtWalk[3]) out.mrtName = cleanName(mrtWalk[3]);
  } else if (mrtNamed) {
    out.mrtName = cleanName(mrtNamed[1]);
    out.mrtCode = mrtNamed[2].toUpperCase();
  }

  // HDB flat type.
  if (isHdb) {
    const room = t.match(/\b([2345])\s?[-\s]?room\b/i);
    if (room) out.flatType = `${room[1]} ROOM`;
    else if (/\bmulti[-\s]?generation\b/i.test(t)) out.flatType = "MULTI-GENERATION";
    else if (/\bexecutive\b/i.test(t) && !/\bcondominium\b/i.test(t)) out.flatType = "EXECUTIVE";
  }

  return out;
}
